import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import chunk from "lodash-es/chunk";
import { Button, Box } from "@material-ui/core";
import {
  PublishOutlined as PublishIcon,
  CancelScheduleSend as CancelPublishIcon,
  FileCopyOutlined as CopyIcon,
} from "@material-ui/icons";

import {
  ADD_MENU,
  UPDATE_PB_MENU,
  DELETE_MENU,
  DELETE_SUBMENU,
  UPDATE_MEMO,
  OPEN_DLG_SUBMENU,
  selectPbMenus,
  selectIsPublished,
  ON_MENU_SAVED,
  selectCurationGroupId,
} from "./reducer";
import { isPbMenu } from "./functions";
import { visibleDays, getDayText, isPastWeek } from "../calendar";
import SubMenuDialog from "./SubMenuDialog";
import ServiceTimeDialog from "./ServiceTimeDialog";
import CustomerListDialog from "./CustomerListDialog";
import PublishDialog from "./PublishDialog";
import CurationCopyDialog from "./CurationCopyDialog";
import SerivcePlanDialog from "./ServicePlanDialog";
import DayView from "./DayView";
import MealBox from "./MealBox";
import EmptyMealBox from "./EmptyMealBox";
import PBMenu from "./PBMenu";
import DontHave from "./DontHave";
import { findParentHasAttribute } from "../util/domUtils";
import styles from "./CustomerOrderCalendar.module.css";

const CustomerOrderCalendar = ({ date }) => {
  const [publishDlgState, setPublishDlgState] = useState({
    open: false,
    week: [],
    publish: false,
  });

  const [curationCopyDlgState, setCurationCopyDlgState] = useState({
    open: false,
    week: [],
  });

  // open publish dialog
  const handleClickPublish = (week, publish = true) => {
    setPublishDlgState({
      open: true,
      week,
      publish,
    });
  };

  // close publish dialog
  const handleClickClose = () => {
    setPublishDlgState({
      open: false,
      week: [],
      publish: false,
    });
  };

  // copy curation
  const handleClickCurationCopy = (week) => {
    // open dialog
    setCurationCopyDlgState({
      open: true,
      week,
    });
  };

  // close curationCopy dialog
  const handleCurationCopyClose = () => {
    setCurationCopyDlgState({
      open: false,
      week: [],
    });
  };

  // get dates of month, weeks
  const datesOfMonth = visibleDays(date);
  const weeks = chunk(datesOfMonth, 7);

  return (
    <div className={styles.calendar}>
      {weeks.map((week, weekIdx) => (
        <WeekView
          key={"week-" + weekIdx}
          week={week}
          onClickPublish={handleClickPublish}
          onClickCurationCopy={handleClickCurationCopy}
        />
      ))}
      <SubMenuDialog />
      <ServiceTimeDialog />
      <CustomerListDialog />
      <SerivcePlanDialog />
      <PublishDialog
        open={publishDlgState.open}
        week={publishDlgState.week}
        publish={publishDlgState.publish}
        onClose={handleClickClose}
      />
      <CurationCopyDialog
        open={curationCopyDlgState.open}
        week={curationCopyDlgState.week}
        onClose={handleCurationCopyClose}
      />
    </div>
  );
};

const WeekView = ({ week, onClickPublish, onClickCurationCopy }) => {
  const dispatch = useDispatch();
  const pbMenus = useSelector(selectPbMenus);
  const pastWeek = isPastWeek(week, 0);
  const isPublised = useSelector((state) => selectIsPublished(state, week));
  const curationGroupId = useSelector(selectCurationGroupId);

  // 메뉴 삭제 처리
  const handleDeleteMenu = (evt) => {
    const targetEl = evt.currentTarget;
    const date = targetEl.getAttribute("data-date");
    const serviceTime = targetEl.getAttribute("data-servicetime");
    const menuId = parseInt(targetEl.getAttribute("data-menuid"), 10);

    dispatch({
      type: DELETE_MENU,
      payload: {
        date,
        serviceTime,
        menuId,
      },
    });
  };

  // 서브 메뉴 삭제 처리
  const handleDeleteSubMenu = (evt) => {
    const targetEl = findParentHasAttribute(
      evt.currentTarget,
      "data-submenuid"
    );
    if (!targetEl) {
      console.error("can't find a button element to delete.");
      return;
    }

    // dispatch a delete action
    const date = targetEl.getAttribute("data-date");
    const serviceTime = targetEl.getAttribute("data-servicetime");
    const menuId = parseInt(targetEl.getAttribute("data-menuid"), 10);
    const subMenuId = parseInt(targetEl.getAttribute("data-submenuid"), 10);

    dispatch({
      type: DELETE_SUBMENU,
      payload: {
        date,
        serviceTime,
        menuId,
        subMenuId,
      },
    });
  };

  // 서브 메뉴 다이얼로그
  const handleOpenDialog = (evt) => {
    const date = evt.currentTarget.getAttribute("data-date");
    const serviceTime = evt.currentTarget.getAttribute("data-servicetime");
    const menuId = parseInt(evt.currentTarget.getAttribute("data-menuid"), 10);

    // dispatch a open action
    dispatch({
      type: OPEN_DLG_SUBMENU,
      payload: {
        date,
        serviceTime,
        menuId,
      },
    });
  };

  // 메모 업데이트
  const handleUpdateMemo = ({ date, serviceTime, menuId, memo }) => {
    dispatch({
      type: UPDATE_MEMO,
      payload: {
        date,
        serviceTime,
        menuId,
        memo,
      },
    });
  };

  // 일반 메뉴 추가
  const handleDropMenu = (evt) => {
    const targetEl = evt.currentTarget;
    if (!targetEl) {
      console.error("can't find a target mealbox");
      return;
    }

    evt.preventDefault();
    const date = targetEl.getAttribute("data-date");
    const serviceTime = targetEl.getAttribute("data-servicetime");
    const menuType = targetEl.getAttribute("data-menutype");
    const selectedMenu = JSON.parse(evt.dataTransfer.getData("text"));

    dispatch({
      type: ADD_MENU,
      payload: {
        date,
        serviceTime,
        menuType,
        selectedMenu,
      },
    });
  };

  const handleSelectPBMenu = ({ date, serviceTime, menu }) => {
    dispatch({
      type: UPDATE_PB_MENU,
      payload: {
        date,
        serviceTime,
        pbMenu: menu,
      },
    });
  };

  const handleClickPublish = () => {
    onClickPublish(week);
  };

  const handleClickCancelPublish = () => {
    onClickPublish(week, false);
  };

  const handleOnMenuSaved = ({ id, date, serviceTime, menuId }) => {
    dispatch({
      type: ON_MENU_SAVED,
      payload: {
        id,
        date,
        serviceTime,
        menuId,
      },
    });
  };

  const renderMenu = (day,serviceTime, menu) => {
    if (menu.menuType === "2") {
      return menu.menuId === 0 ? null : <DontHave />;
    } else if (isPbMenu(menu)) {
      return (
        <PBMenu
          day={day}
          serviceTime={serviceTime}
          menu={menu}
          pbMenus={pbMenus}
          onSelectMenu={handleSelectPBMenu}
          onUpdateMemo={handleUpdateMemo}
        />
      );
    } else if (menu.menu) {
      return (
        <MealBox
          day={day}
          serviceTime={serviceTime}
          menu={menu}
          onClickDeleteMenu={handleDeleteMenu}
          onClickAddSubMenu={handleOpenDialog}
          onClickDeleteSubMenu={handleDeleteSubMenu}
          onUpdateMemo={handleUpdateMemo}
          onSaved={handleOnMenuSaved}
        />
      );
    } else {
      return (
        <EmptyMealBox 
          day={day}
          serviceTime={serviceTime}
          menu={menu} 
          onDropMenu={handleDropMenu} />
      );
    }
  };

  const handleCurationCopy = () => {
    onClickCurationCopy(week);
  };

  return (
    <div className={styles.weekRow}>
      {/* bg */}
      <div className={styles.weekRowBg}>
        {week.map((day, i) => (
          <div key={i} className={styles.dayBg}></div>
        ))}
      </div>
      {/* header */}
      <div className={styles.weekHeaderRow}>
        {week.map((day, i) => (
          <WeekHeader key={i} day={day} />
        ))}
      </div>
      {/* day meals */}
      <div className={styles.dayRow}>
        {week.map((day, i) => (
          <DayView key={i} day={day} renderMenu={renderMenu} />
        ))}
      </div>
      {/* footer */}
      <div className={styles.weekFooterRow}>
        <div className={styles.weekSummary}>
          {isPublised ? (
            <Button
              variant="text"
              color="secondary"
              startIcon={<CancelPublishIcon />}
              onClick={handleClickCancelPublish}
              disabled={pastWeek}
            >
              {pastWeek ? "Published" : "Cancle Published"}
            </Button>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "50px",
              }}
            >
              {curationGroupId ? (
                <Button
                  variant="text"
                  color="success"
                  startIcon={<CopyIcon />}
                  onClick={handleCurationCopy}
                >
                  <span>{curationGroupId}</span>번 큐레이션 그룹에 메뉴 내보내기
                </Button>
              ) : null}

              <Button
                variant="text"
                color="primary"
                startIcon={<PublishIcon />}
                onClick={handleClickPublish}
              >
                Publish
              </Button>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

const WeekHeader = ({ day }) => {
  return (
    <div className={styles.weekHeader}>
      <div className={styles.date}>{getDayText(day, false)}</div>
      <div className={styles.date}>{day.getDate()}</div>
    </div>
  );
};

export default CustomerOrderCalendar;
